import React, { useState } from 'react';
import { IconArrowForward, IconCircleCheck, IconClockPause } from '@tabler/icons-react';
import { osAssignedAgentId } from '../utils/boardChatUtils';

const MAX_VISIBLE = 3;

function equipTipFromOsItem(item) {
  const eq = item?.osData?.equipamento || item?.os_data?.equipamento || {};
  const modelo = eq.modelo ? String(eq.modelo).trim() : '';
  const serie = String(eq.num_serie || eq.serie || eq.numSerie || '').trim();
  const patrimonio = String(eq.patrimonio || eq.tag || '').trim();
  const metaBits = [
    serie ? `Série ${serie}` : null,
    patrimonio ? `Pat ${patrimonio}` : null,
  ].filter(Boolean);
  return {
    modelo,
    meta: metaBits.join(' · '),
  };
}

function agentNameById(agents, agentId) {
  const id = Number(agentId);
  if (!Number.isFinite(id) || id <= 0) return null;
  for (const a of Array.isArray(agents) ? agents : []) {
    const aid = Number(a?.id ?? a?.user_id);
    if (aid !== id) continue;
    const name = String(a?.name || a?.available_name || a?.email || '').trim();
    if (name) return name;
  }
  return null;
}

/**
 * Chips de OS abaixo do item da lista (pré-atendimento).
 * Máx. 3 visíveis; restante via "Ver mais".
 * Hover: status, atendente (se houver), modelo + série/pat.
 */
export function BoardChatConvOsChips({
  osList = [],
  agents = [],
  activeOsId = null,
  conversationActive = false,
  onSelectOs,
  tipProps,
}) {
  const [expanded, setExpanded] = useState(false);
  const items = Array.isArray(osList)
    ? osList
        .map((item) => {
          const osId = String(item?.osId || item?.os_id || '').trim();
          if (!osId) return null;
          const equip = equipTipFromOsItem(item);
          const assignedId = osAssignedAgentId(item);
          const assigneeName =
            assignedId != null
              ? agentNameById(agents, assignedId) || `agente #${assignedId}`
              : null;
          return {
            osId,
            status: String(item?.status || 'pending').toLowerCase(),
            osData: item?.osData || item?.os_data || null,
            modelo: equip.modelo,
            meta: equip.meta,
            assigneeName,
            unclaimed: Boolean(item?.unclaimed),
          };
        })
        .filter(Boolean)
    : [];

  if (items.length < 1) return null;

  const visible = expanded ? items : items.slice(0, MAX_VISIBLE);
  const hiddenCount = items.length - MAX_VISIBLE;

  return (
    <div
      className="bch-conv-os-row"
      role="group"
      aria-label="OS vinculadas"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {visible.map((item) => {
        const active =
          conversationActive &&
          activeOsId != null &&
          String(activeOsId) === item.osId;
        const statusLabel = item.unclaimed
          ? 'Nova · sem atendente'
          : item.status === 'forwarded'
            ? 'Encaminhada'
            : item.status === 'closed'
              ? 'Fechada'
              : item.status === 'snoozed'
                ? 'Adiada'
                : 'Pendente';
        const tipRows = [
          { label: 'Status', value: statusLabel },
          item.assigneeName ? { label: 'Atendente', value: item.assigneeName } : null,
          item.modelo ? { label: 'Equipamento', value: item.modelo } : null,
          item.meta ? { label: 'Identificação', value: item.meta } : null,
        ].filter(Boolean);
        const tipContent = {
          title: `OS ${item.osId}`,
          rows: tipRows,
        };
        const tip =
          typeof tipProps === 'function' ? tipProps(tipContent, 'above') : null;
        return (
          <button
            key={item.osId}
            type="button"
            className={`bch-conv-os-chip${active ? ' is-active' : ''}${
              item.unclaimed
                ? ' bch-conv-os-chip--unclaimed'
                : item.status !== 'pending'
                  ? ` bch-conv-os-chip--${item.status}`
                  : ''
            }`}
            aria-pressed={active}
            aria-label={`Selecionar OS ${item.osId} · ${statusLabel}${
              item.assigneeName ? ` · ${item.assigneeName}` : ''
            }`}
            onClick={() => onSelectOs?.(item.osId)}
            {...(tip || {})}
          >
            <span className="bch-conv-os-chip-num">{item.osId}</span>
            {item.unclaimed ? (
              <span className="bch-conv-os-chip-unclaimed-dot" aria-hidden />
            ) : item.status === 'forwarded' ? (
              <IconArrowForward
                size={11}
                stroke={2}
                className="bch-conv-os-chip-status-ico"
                aria-hidden
              />
            ) : item.status === 'closed' ? (
              <IconCircleCheck
                size={11}
                stroke={2}
                className="bch-conv-os-chip-status-ico"
                aria-hidden
              />
            ) : item.status === 'snoozed' ? (
              <IconClockPause
                size={11}
                stroke={2}
                className="bch-conv-os-chip-status-ico"
                aria-hidden
              />
            ) : null}
          </button>
        );
      })}
      {!expanded && hiddenCount > 0 ? (
        <button
          type="button"
          className="bch-conv-os-more"
          aria-label={`Ver mais ${hiddenCount} OS`}
          onClick={() => setExpanded(true)}
        >
          Ver mais · {hiddenCount}
        </button>
      ) : null}
      {expanded && hiddenCount > 0 ? (
        <button
          type="button"
          className="bch-conv-os-more"
          aria-label="Ver menos OS"
          onClick={() => setExpanded(false)}
        >
          Ver menos
        </button>
      ) : null}
    </div>
  );
}
